import React, { useState, useEffect, useMemo } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import {
  RefreshCw,
  Plus,
  LogOut,
  Users,
  Clock,
  Activity,
  ChevronRight,
  Loader2,
  Filter,
  Calendar,
} from 'lucide-react';
import {
  useRealtimeDashboard,
  useCurrentlyCheckedIn,
  useCheckIn,
  useCheckOut,
} from '../../hooks/useAttendance';
import { useClasses } from '../../hooks/useClasses';
import { useStudents } from '../../hooks/useStudents';
import { AttendanceStats, CheckInModal } from '../../components/attendance';

/**
 * AttendanceDashboard - Real-time attendance dashboard
 */
const AttendanceDashboard = () => {
  const navigate = useNavigate();
  const { tenantId } = useParams();

  // State
  const [selectedClass, setSelectedClass] = useState('all');
  const [showCheckInModal, setShowCheckInModal] = useState(false);
  const [lastRefresh, setLastRefresh] = useState(new Date());

  // Fetch data
  const { data: dashboardData, isLoading: dashboardLoading, refetch: refetchDashboard } = useRealtimeDashboard();
  const { data: checkedInData, isLoading: checkedInLoading, refetch: refetchCheckedIn } = useCurrentlyCheckedIn(
    selectedClass !== 'all' ? { classId: selectedClass } : {}
  );
  const { data: classesData } = useClasses();
  const { data: studentsData } = useStudents();

  // Mutations
  const checkInMutation = useCheckIn();
  const checkOutMutation = useCheckOut();

  // Extract data - wrapped in useMemo to avoid recreating references on every render
  const stats = useMemo(() => dashboardData?.stats || {}, [dashboardData?.stats]);
  const currentlyCheckedIn = useMemo(() => checkedInData?.students || [], [checkedInData?.students]);
  const recentActivity = useMemo(() => dashboardData?.recentActivity || [], [dashboardData?.recentActivity]);
  const classes = useMemo(() => classesData?.classes || [], [classesData?.classes]);
  const students = useMemo(() => studentsData?.students || [], [studentsData?.students]);

  // Auto-refresh every 60 seconds
  useEffect(() => {
    const interval = setInterval(() => {
      refetchDashboard();
      refetchCheckedIn();
      setLastRefresh(new Date());
    }, 60000);

    return () => clearInterval(interval);
  }, [refetchDashboard, refetchCheckedIn]);

  // Filter checked-in students by selected class
  const filteredCheckedIn = useMemo(() => {
    if (selectedClass === 'all') return currentlyCheckedIn;
    return currentlyCheckedIn.filter(
      (record) => record.classId === selectedClass
    );
  }, [currentlyCheckedIn, selectedClass]);

  // Handlers
  const handleRefresh = () => {
    refetchDashboard();
    refetchCheckedIn();
    setLastRefresh(new Date());
  };

  const handleCheckIn = async (formData) => {
    try {
      await checkInMutation.mutateAsync({
        studentId: formData.studentId,
        classId: selectedClass !== 'all' ? selectedClass : undefined,
        checkInTime: formData.checkInTime,
        method: formData.method,
        notes: formData.notes,
      });
      setShowCheckInModal(false);
    } catch (error) {
      console.error('Check-in failed:', error);
      alert('Failed to check in: ' + error.message);
    }
  };

  const handleCheckOut = async (student) => {
    if (!window.confirm(`Check out ${student.firstName} ${student.lastName}?`)) {
      return;
    }

    try {
      await checkOutMutation.mutateAsync({
        studentId: student.id,
        classId: selectedClass !== 'all' ? selectedClass : undefined,
      });
    } catch (error) {
      console.error('Check-out failed:', error);
      alert('Failed to check out: ' + error.message);
    }
  };

  const handleViewClassAttendance = (classId) => {
    navigate(`/${tenantId}/dashboard/attendance/class/${classId}`);
  };

  // Format time ago
  const formatTimeAgo = (timestamp) => {
    if (!timestamp) return '';
    const date = new Date(timestamp);
    const now = new Date();
    const diffMs = now - date;
    const diffMins = Math.floor(diffMs / 60000);
    const diffHours = Math.floor(diffMs / 3600000);

    if (diffMins < 1) return 'Just now';
    if (diffMins < 60) return `${diffMins} min ago`;
    if (diffHours < 24) return `${diffHours} hour ago`;
    return date.toLocaleDateString();
  };

  // Format time
  const formatTime = (timestamp) => {
    if (!timestamp) return '-';
    return new Date(timestamp).toLocaleTimeString([], {
      hour: '2-digit',
      minute: '2-digit',
    });
  };

  const isLoading = dashboardLoading || checkedInLoading;

  return (
    <div className="animate-fade-in h-full flex flex-col">
      {/* Header */}
      <div className="mb-6">
        <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-4">
          <div>
            <h1 className="text-3xl font-bold text-gray-900">
              Attendance Dashboard
            </h1>
            <p className="text-gray-500 mt-1">
              Real-time attendance tracking for your classes.
            </p>
          </div>

          {/* Quick Actions */}
          <div className="flex items-center space-x-3">
            <button
              onClick={handleRefresh}
              className="flex items-center space-x-2 px-4 py-2 bg-white border border-gray-200 rounded-lg text-gray-700 hover:bg-gray-50 transition-colors"
              disabled={isLoading}
            >
              <RefreshCw size={18} className={isLoading ? 'animate-spin' : ''} />
              <span>Refresh</span>
            </button>
            <button
              onClick={() => setShowCheckInModal(true)}
              className="flex items-center space-x-2 px-4 py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700 transition-colors"
            >
              <Plus size={18} />
              <span>Quick Check In</span>
            </button>
          </div>
        </div>

        {/* Last refresh time */}
        <p className="text-xs text-gray-400 mt-2">
          Last updated: {lastRefresh.toLocaleTimeString()} (Auto-refreshes every
          minute)
        </p>
      </div>

      {/* Stats */}
      <div className="mb-6">
        <AttendanceStats stats={stats} isLoading={dashboardLoading} />
      </div>

      {/* Class Filter */}
      <div className="mb-6 flex flex-col sm:flex-row gap-4">
        <div className="flex items-center space-x-2 bg-white px-4 py-2 rounded-lg border border-gray-200">
          <Filter size={18} className="text-gray-400" />
          <select
            value={selectedClass}
            onChange={(e) => setSelectedClass(e.target.value)}
            className="outline-none text-gray-700 bg-transparent w-full sm:min-w-[200px]"
          >
            <option value="all">All Classes</option>
            {classes.map((cls) => (
              <option key={cls.id} value={cls.id}>
                {cls.name}
              </option>
            ))}
          </select>
        </div>

        <button
          onClick={() =>
            selectedClass !== 'all'
              ? handleViewClassAttendance(selectedClass)
              : navigate(`/${tenantId}/dashboard/attendance/class/all`)
          }
          className="flex items-center space-x-2 text-purple-600 hover:text-purple-700 font-medium"
        >
          <Calendar size={18} />
          <span>View Class Attendance</span>
          <ChevronRight size={16} />
        </button>
      </div>

      {/* Main Content */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 flex-1">
        {/* Currently Checked In */}
        <div className="lg:col-span-2">
          <div className="bg-white rounded-xl shadow-sm border border-gray-200 h-full">
            <div className="px-6 py-4 border-b border-gray-100 flex items-center justify-between">
              <div className="flex items-center space-x-2">
                <Users size={20} className="text-purple-600" />
                <h2 className="text-lg font-semibold text-gray-900">
                  Currently Checked In
                </h2>
              </div>
              <span className="bg-purple-100 text-purple-700 px-3 py-1 rounded-full text-sm font-medium">
                {filteredCheckedIn.length}
              </span>
            </div>

            <div className="p-6">
              {checkedInLoading ? (
                <div className="flex items-center justify-center py-12">
                  <Loader2 className="animate-spin text-purple-600" size={32} />
                </div>
              ) : filteredCheckedIn.length === 0 ? (
                <div className="text-center py-12">
                  <Users size={48} className="mx-auto text-gray-300 mb-4" />
                  <p className="text-gray-500">No students currently checked in</p>
                  <button
                    onClick={() => setShowCheckInModal(true)}
                    className="mt-4 text-purple-600 hover:text-purple-700 font-medium"
                  >
                    Check in a student
                  </button>
                </div>
              ) : (
                <div className="space-y-3">
                  {filteredCheckedIn.map((record) => (
                    <div
                      key={record.id}
                      className="flex items-center justify-between p-4 bg-gray-50 rounded-lg hover:bg-gray-100 transition-colors"
                    >
                      <div className="flex items-center space-x-4">
                        <div className="w-10 h-10 bg-purple-100 rounded-full flex items-center justify-center">
                          <span className="text-purple-700 font-semibold text-sm">
                            {record.student?.firstName?.[0] || ''}
                            {record.student?.lastName?.[0] || ''}
                          </span>
                        </div>
                        <div>
                          <p className="font-medium text-gray-900">
                            {record.student?.firstName} {record.student?.lastName}
                          </p>
                          <p className="text-sm text-gray-500">
                            {record.class?.name || 'No class'}
                          </p>
                        </div>
                      </div>

                      <div className="flex items-center space-x-4">
                        <div className="text-right">
                          <div className="flex items-center text-sm text-gray-500">
                            <Clock size={14} className="mr-1" />
                            <span>In: {formatTime(record.checkInTime)}</span>
                          </div>
                          <p className="text-xs text-gray-400">
                            {formatTimeAgo(record.checkInTime)}
                          </p>
                        </div>
                        <button
                          onClick={() => handleCheckOut(record.student)}
                          className="flex items-center space-x-1 px-3 py-1.5 text-sm font-medium text-blue-700 bg-blue-100 hover:bg-blue-200 rounded transition-colors"
                        >
                          <LogOut size={14} />
                          <span>Check Out</span>
                        </button>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
        </div>

        {/* Recent Activity */}
        <div className="lg:col-span-1">
          <div className="bg-white rounded-xl shadow-sm border border-gray-200 h-full">
            <div className="px-6 py-4 border-b border-gray-100 flex items-center space-x-2">
              <Activity size={20} className="text-purple-600" />
              <h2 className="text-lg font-semibold text-gray-900">
                Recent Activity
              </h2>
            </div>

            <div className="p-6">
              {dashboardLoading ? (
                <div className="flex items-center justify-center py-12">
                  <Loader2 className="animate-spin text-purple-600" size={32} />
                </div>
              ) : recentActivity.length === 0 ? (
                <div className="text-center py-12">
                  <Activity size={48} className="mx-auto text-gray-300 mb-4" />
                  <p className="text-gray-500">No recent activity</p>
                </div>
              ) : (
                <div className="space-y-4">
                  {recentActivity.map((activity, index) => (
                    <div
                      key={index}
                      className="flex items-start space-x-3 pb-4 border-b border-gray-100 last:border-0 last:pb-0"
                    >
                      <div
                        className={`w-2 h-2 rounded-full mt-2 ${
                          activity.type === 'check_in'
                            ? 'bg-green-500'
                            : activity.type === 'check_out'
                            ? 'bg-blue-500'
                            : activity.type === 'absent'
                            ? 'bg-red-500'
                            : 'bg-gray-500'
                        }`}
                      />
                      <div className="flex-1">
                        <p className="text-sm text-gray-900">
                          <span className="font-medium">
                            {activity.student?.firstName} {activity.student?.lastName}
                          </span>{' '}
                          {activity.type === 'check_in'
                            ? 'checked in'
                            : activity.type === 'check_out'
                            ? 'checked out'
                            : activity.type === 'marked_absent'
                            ? 'marked absent'
                            : activity.type === 'marked_late'
                            ? 'marked late'
                            : 'updated'}
                        </p>
                        <p className="text-xs text-gray-500">
                          {activity.class?.name || 'No class'} •{' '}
                          {formatTimeAgo(activity.timestamp)}
                        </p>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* Check In Modal */}
      <CheckInModal
        isOpen={showCheckInModal}
        onClose={() => setShowCheckInModal(false)}
        onSubmit={handleCheckIn}
        students={students}
        isLoading={checkInMutation.isPending}
      />
    </div>
  );
};

export default AttendanceDashboard;
